import { useEffect, useCallback } from "react";
import { getCleanUrlForNewWindow } from "src/util/embedUtils";
import useEmbedMode from "./useEmbedMode";

/**
 * Hook that provides global link interception for embed mode
 * This ensures all links open in new windows when in embed mode
 */
const useEmbedLinkInterceptor = () => {
  const { isEmbedMode } = useEmbedMode();

  // eslint-disable-next-line consistent-return
  const handleLinkClick = useCallback(
    (event) => {
      if (!isEmbedMode) {
        return;
      }

      // Only handle anchor tags and elements with href attributes
      const target = event.target.closest("a");
      if (!target || !target.href) {
        return;
      }

      // Skip if the link already has target="_blank" or is an anchor link
      if (target.target === "_blank" || target.href.includes("#")) {
        return;
      }

      // Skip if it's an external link that should open in new window anyway
      if (
        target.href.startsWith("http") &&
        !target.href.includes(window.location.hostname)
      ) {
        return;
      }

      // Prevent default behavior and open in new window
      event.preventDefault();
      event.stopPropagation();

      const cleanUrl = getCleanUrlForNewWindow(target.href);
      window.open(cleanUrl, "_blank", "noopener,noreferrer");
    },
    [isEmbedMode]
  );

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!isEmbedMode) {
      return;
    }

    // Add global click listener for link interception
    document.addEventListener("click", handleLinkClick, true);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [isEmbedMode, handleLinkClick]);

  // eslint-disable-next-line consistent-return
  return {
    isEmbedMode,
  };
};

export default useEmbedLinkInterceptor;
